/**
 * Inside one Data Room location — a category root or a user folder.
 *
 * The page states where you are twice, deliberately: the breadcrumb walks
 * the whole chain from the Data Room root, and the page title is the
 * current folder. Contents render folders first, then documents, and the
 * folder verbs — create, rename, move, delete — are plain buttons opening
 * the shared accessible dialog, not drag targets; V1 trades slickness for
 * something a keyboard drives completely.
 *
 * Every rule the dialogs appear to enforce — depth, cycles, sibling names,
 * emptiness before delete — is enforced again by the server inside the
 * mutation transaction. The client filters the destination list as a
 * courtesy so people rarely see a refusal; the server's answer is the wall.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  DATA_ROOM_CATEGORIES,
  DATA_ROOM_MAX_FOLDER_DEPTH,
  DATA_ROOM_MAX_FOLDER_NAME_LENGTH,
  isDataRoomCategory,
} from '@mouja/domain/data-room';

import {
  ApiFailure,
  createDataRoomFolder,
  deleteDataRoomFolder,
  fetchDataRoom,
  moveDataRoomDocument,
  moveDataRoomFolder,
  newIdempotencyKey,
  renameDataRoomFolder,
  type DataRoomDocumentSummary,
  type DataRoomFolder,
} from '../api.js';
import { EventTime, useWorkspaceCopy } from '../components.js';
import { useSession } from '../session.js';
import {
  Button,
  ChoiceGroup,
  ConfirmDialog,
  InlineAlert,
  TextField,
  useToasts,
} from '../ui.js';
import { workspaceSlots } from '../slots.js';
import { FailurePanel, LoadingPanel, SectionHeading, useAsync } from './common.js';
import { NotAvailablePage } from './NotAvailablePage.js';

type DialogKind =
  'newFolder' | 'renameFolder' | 'moveFolder' | 'deleteFolder' | 'moveDocument';

/** A destination encoded for the radio group: a category root or a folder. */
function encodeDestination(category: string, folderId: string | null): string {
  return folderId === null ? `c:${category}` : `f:${folderId}`;
}

export function DataRoomFolderPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const { session } = useSession();
  const { announce } = useToasts();
  const params = useParams<{ category?: string; id?: string }>();

  const load = useCallback(() => fetchDataRoom(), []);
  const { state, reload } = useAsync(load);

  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const [dialogKey, setDialogKey] = useState('');
  const [targetFolder, setTargetFolder] = useState<DataRoomFolder | null>(null);
  const [targetDocument, setTargetDocument] = useState<DataRoomDocumentSummary | null>(
    null,
  );
  const [nameField, setNameField] = useState('');
  const [destination, setDestination] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const folders = state.status === 'ready' ? state.value.folders : [];
  const documents = state.status === 'ready' ? state.value.documents : [];

  const byId = useMemo(
    () => new Map(folders.map((folder) => [folder.id, folder])),
    [folders],
  );

  /** The chain from the category root down to a folder, both ends included. */
  const chainOf = useCallback(
    (folder: DataRoomFolder): readonly DataRoomFolder[] => {
      let chain: DataRoomFolder[] = [folder];
      let cursor = folder;
      while (
        cursor.parentFolderId !== null &&
        chain.length < DATA_ROOM_MAX_FOLDER_DEPTH
      ) {
        const parent = byId.get(cursor.parentFolderId);
        if (parent === undefined) break;
        chain = [parent, ...chain];
        cursor = parent;
      }
      return chain;
    },
    [byId],
  );

  /** Depth beneath the Data Room root: a category root is 1. */
  const depthOf = useCallback(
    (folder: DataRoomFolder): number => 1 + chainOf(folder).length,
    [chainOf],
  );

  const subtreeIdsOf = useCallback(
    (folderId: string): ReadonlySet<string> => {
      const ids = new Set<string>([folderId]);
      let grew = true;
      while (grew) {
        grew = false;
        for (const folder of folders) {
          if (
            folder.parentFolderId !== null &&
            ids.has(folder.parentFolderId) &&
            !ids.has(folder.id)
          ) {
            ids.add(folder.id);
            grew = true;
          }
        }
      }
      return ids;
    },
    [folders],
  );

  // ----------------------------------------------------------- the location //

  const category =
    params.category !== undefined && isDataRoomCategory(params.category)
      ? params.category
      : null;
  const currentFolder = params.id === undefined ? null : (byId.get(params.id) ?? null);

  // Server-computed capability mirrors. They decide what is drawn; the
  // service remains the gate for every request.
  const mayMoveFolders =
    session.status === 'authenticated' && session.profile.mayAdministerTasks;
  const mayDeleteFolders =
    session.status === 'authenticated' && session.profile.mayDeleteDataRoomFolders;

  if (session.status === 'authenticated' && !session.profile.mayUseDataRoom) {
    return <NotAvailablePage />;
  }
  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed') {
    if (state.failure.kind === 'forbidden' || state.failure.kind === 'missing') {
      return <NotAvailablePage />;
    }
    return <FailurePanel failure={state.failure} onRetry={reload} />;
  }
  // An unknown category segment or folder id is the shared missing page.
  if (category === null && currentFolder === null) return <NotAvailablePage />;

  const locationCategory =
    currentFolder === null ? (category ?? '') : currentFolder.category;
  const locationFolderId = currentFolder === null ? null : currentFolder.id;
  const locationName =
    currentFolder === null
      ? (copy.dataRoom.categories[locationCategory] ?? locationCategory)
      : currentFolder.name;

  const subfolders = folders
    .filter((folder) =>
      locationFolderId === null
        ? folder.parentFolderId === null && folder.category === locationCategory
        : folder.parentFolderId === locationFolderId,
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  const contents = documents
    .filter((document) =>
      locationFolderId === null
        ? document.folderId === null && document.category === locationCategory
        : document.folderId === locationFolderId,
    )
    .sort((a, b) => a.title.localeCompare(b.title));

  const crumbs: readonly { readonly to: string | null; readonly label: string }[] = [
    { to: '/data-room', label: copy.dataRoom.title },
    ...(currentFolder === null
      ? [{ to: null, label: locationName }]
      : [
          {
            to: `/data-room/category/${locationCategory}`,
            label: copy.dataRoom.categories[locationCategory] ?? locationCategory,
          },
          ...chainOf(currentFolder).map((folder, index, chain) => ({
            to: index === chain.length - 1 ? null : `/data-room/folder/${folder.id}`,
            label: folder.name,
          })),
        ]),
  ];

  // ------------------------------------------------------------ the dialogs //

  const openDialog = (
    kind: DialogKind,
    options: {
      folder?: DataRoomFolder;
      document?: DataRoomDocumentSummary;
    } = {},
  ): void => {
    setDialog(kind);
    setDialogKey(newIdempotencyKey());
    setFailure(null);
    setTargetFolder(options.folder ?? null);
    setTargetDocument(options.document ?? null);
    setNameField(kind === 'renameFolder' ? (options.folder?.name ?? '') : '');
    setDestination(null);
  };

  const closeDialog = (): void => {
    if (!busy) {
      setDialog(null);
      setFailure(null);
    }
  };

  const decodedDestination = (): { category?: string; folderOrParentId?: string } => {
    if (destination === null) return {};
    if (destination.startsWith('c:')) return { category: destination.slice(2) };
    return { folderOrParentId: destination.slice(2) };
  };

  const act = (): void => {
    setBusy(true);
    setFailure(null);

    const run = async (): Promise<void> => {
      if (dialog === 'newFolder') {
        if (nameField.trim().length === 0)
          throw new ApiFailure('invalid', {
            detail: copy.dataRoom.folderNameMissing,
          });
        await createDataRoomFolder(dialogKey, {
          name: nameField.trim(),
          ...(locationFolderId === null
            ? { category: locationCategory }
            : { parentFolderId: locationFolderId }),
        });
        announce(copy.dataRoom.toastFolderCreated);
        return;
      }
      if (dialog === 'renameFolder' && targetFolder !== null) {
        if (nameField.trim().length === 0)
          throw new ApiFailure('invalid', {
            detail: copy.dataRoom.folderNameMissing,
          });
        await renameDataRoomFolder(dialogKey, targetFolder.id, {
          expectedVersion: targetFolder.version,
          name: nameField.trim(),
        });
        announce(copy.dataRoom.toastFolderRenamed);
        return;
      }
      if (dialog === 'moveFolder' && targetFolder !== null) {
        const decoded = decodedDestination();
        await moveDataRoomFolder(dialogKey, targetFolder.id, {
          expectedVersion: targetFolder.version,
          ...(decoded.category !== undefined ? { category: decoded.category } : {}),
          ...(decoded.folderOrParentId !== undefined
            ? { parentFolderId: decoded.folderOrParentId }
            : {}),
        });
        announce(copy.dataRoom.toastFolderMoved);
        return;
      }
      if (dialog === 'deleteFolder' && targetFolder !== null) {
        await deleteDataRoomFolder(dialogKey, targetFolder.id, {
          expectedVersion: targetFolder.version,
        });
        announce(copy.dataRoom.toastFolderDeleted);
        return;
      }
      if (dialog === 'moveDocument' && targetDocument !== null) {
        const decoded = decodedDestination();
        await moveDataRoomDocument(dialogKey, targetDocument.id, {
          expectedVersion: targetDocument.version,
          ...(decoded.category !== undefined ? { category: decoded.category } : {}),
          ...(decoded.folderOrParentId !== undefined
            ? { folderId: decoded.folderOrParentId }
            : {}),
        });
        announce(copy.dataRoom.toastDocumentMoved);
        return;
      }
    };

    run()
      .then(() => {
        setBusy(false);
        setDialog(null);
        reload();
      })
      .catch((thrown: unknown) => {
        setBusy(false);
        setFailure(thrown instanceof ApiFailure ? thrown : new ApiFailure('failed'));
      });
  };

  const failureAlert =
    failure === null ? null : (
      <InlineAlert
        tone="error"
        title={
          dialog === 'deleteFolder'
            ? copy.dataRoom.deleteFolderFailedTitle
            : failure.kind === 'conflict'
              ? copy.states.conflictTitle
              : copy.states.saveFailedTitle
        }
        role="alert"
      >
        {failure.detail ??
          (failure.kind === 'conflict'
            ? copy.states.conflictBody
            : copy.states.saveFailedBody)}
      </InlineAlert>
    );

  /** Where things may move to. The server re-checks every rule. */
  const destinationChoices = (options: {
    readonly movingFolder: DataRoomFolder | null;
  }): readonly { value: string; label: string }[] => {
    const excluded =
      options.movingFolder === null
        ? new Set<string>()
        : subtreeIdsOf(options.movingFolder.id);
    const height =
      options.movingFolder === null
        ? 0
        : Math.max(
            ...[...excluded].map((id) =>
              depthOf(byId.get(id) ?? (options.movingFolder as DataRoomFolder)),
            ),
          ) -
          depthOf(options.movingFolder) +
          1;

    const roots = DATA_ROOM_CATEGORIES.map((value) => ({
      value: encodeDestination(value, null),
      label: copy.dataRoom.categories[value] ?? value,
    }));
    const nested = folders
      .filter((folder) => !excluded.has(folder.id))
      .filter((folder) => depthOf(folder) + height <= DATA_ROOM_MAX_FOLDER_DEPTH)
      .map((folder) => ({
        value: encodeDestination(folder.category, folder.id),
        label: [
          copy.dataRoom.categories[folder.category] ?? folder.category,
          ...chainOf(folder).map((ancestor) => ancestor.name),
        ].join(' / '),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...roots, ...nested];
  };

  // -------------------------------------------------------------- rendering //

  return (
    <>
      <nav
        aria-label={copy.dataRoom.breadcrumbLabel}
        style={{ marginBottom: 'var(--space-sm)' }}
      >
        <ol
          style={{
            listStyle: 'none',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
            padding: 0,
            margin: 0,
            font: 'var(--type-body-md)',
          }}
        >
          {crumbs.map((crumb, index) => (
            <li key={index} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {index > 0 ? (
                <span aria-hidden="true" style={{ color: 'var(--text-muted)' }}>
                  /
                </span>
              ) : null}
              {crumb.to === null ? (
                <span aria-current="page" style={{ color: 'var(--text-primary)' }}>
                  {crumb.label}
                </span>
              ) : (
                <Link to={crumb.to} style={{ color: 'var(--text-link)' }}>
                  {crumb.label}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </nav>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 'var(--space-md)',
          flexWrap: 'wrap',
          marginBottom: 'var(--space-lg)',
        }}
      >
        <h1
          style={{
            font: 'var(--type-display-md)',
            letterSpacing: 'var(--type-display-md-tracking)',
            color: 'var(--text-primary)',
            margin: 0,
          }}
        >
          {locationName}
        </h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button
            variant="secondary"
            onClick={() => {
              openDialog('newFolder');
            }}
          >
            {copy.dataRoom.newFolder}
          </Button>
          <Link
            to={`/data-room/new?${
              locationFolderId === null
                ? `category=${locationCategory}`
                : `folder=${locationFolderId}`
            }`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 44,
              padding: '0 20px',
              borderRadius: 'var(--radius-button)',
              background: 'var(--action-primary)',
              color: 'var(--text-on-primary)',
              font: 'var(--type-button-md)',
              textDecoration: 'none',
            }}
          >
            {copy.dataRoom.newDocument}
          </Link>
        </div>
      </div>

      {subfolders.length === 0 && contents.length === 0 ? (
        <p style={{ font: 'var(--type-body-md)', color: 'var(--text-muted)' }}>
          {copy.dataRoom.emptyLocation}
        </p>
      ) : null}

      {subfolders.length > 0 ? (
        <section style={{ marginBottom: 'var(--space-xl)' }}>
          <SectionHeading>{copy.dataRoom.subfoldersHeading}</SectionHeading>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {subfolders.map((folder) => (
              <li
                key={folder.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                  padding: '10px 0',
                  borderBlockEnd: '1px solid var(--border-hairline)',
                }}
              >
                <Link
                  to={`/data-room/folder/${folder.id}`}
                  style={{
                    color: 'var(--text-link)',
                    font: 'var(--type-body-md)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                  }}
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    aria-hidden="true"
                  >
                    <path d="M3 6a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  </svg>
                  {folder.name}
                </Link>
                <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  <Button
                    size="sm"
                    variant="quiet"
                    onClick={() => {
                      openDialog('renameFolder', { folder });
                    }}
                  >
                    {copy.dataRoom.renameFolder}
                  </Button>
                  {/* Moving a folder to an arbitrary destination remains
                      Owner-only. Owner and Developer may recursively delete
                      a folder tree; documents are archived server-side. */}
                  {mayMoveFolders ? (
                    <Button
                      size="sm"
                      variant="quiet"
                      onClick={() => {
                        openDialog('moveFolder', { folder });
                      }}
                    >
                      {copy.dataRoom.moveFolder}
                    </Button>
                  ) : null}
                  {mayDeleteFolders ? (
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() => {
                        openDialog('deleteFolder', { folder });
                      }}
                    >
                      {copy.dataRoom.deleteFolder}
                    </Button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {contents.length > 0 ? (
        <section>
          <SectionHeading>{copy.dataRoom.documentsHeading}</SectionHeading>
          <div style={{ overflowX: 'auto' }}>
            <table className="mj-table">
              <thead>
                <tr>
                  <th scope="col">{copy.dataRoom.columns.title}</th>
                  <th scope="col">{copy.dataRoom.columns.version}</th>
                  <th scope="col">{copy.dataRoom.columns.updatedBy}</th>
                  <th scope="col">{copy.dataRoom.columns.updated}</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {contents.map((document) => (
                  <tr key={document.id}>
                    <td>
                      <span
                        style={{
                          display: 'inline-flex',
                          gap: 8,
                          alignItems: 'baseline',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Link
                          to={`/data-room/${document.id}`}
                          style={{
                            color: 'var(--text-link)',
                            font: 'var(--type-body-md)',
                          }}
                        >
                          {document.title}
                        </Link>
                        {workspaceSlots().documentBadge?.({
                          audience: document.audience,
                        })}
                      </span>
                    </td>
                    <td>
                      <span
                        className="mj-ltr"
                        dir="ltr"
                        style={{ font: 'var(--type-body-tabular)' }}
                      >
                        {copy.dataRoom.versionLabel(document.version)}
                      </span>
                    </td>
                    <td>{document.updatedBy.displayName}</td>
                    <td>
                      <EventTime iso={document.updatedAt} />
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="quiet"
                        onClick={() => {
                          openDialog('moveDocument', { document });
                        }}
                      >
                        {copy.dataRoom.moveDocument}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}

      <ConfirmDialog
        open={dialog === 'newFolder'}
        title={copy.dataRoom.newFolder}
        body={copy.dataRoom.newFolderBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.newFolderEffects}
        confirmLabel={copy.dataRoom.newFolder}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={act}
      >
        <TextField
          label={copy.dataRoom.folderNameLabel}
          value={nameField}
          onChange={setNameField}
          maxLength={DATA_ROOM_MAX_FOLDER_NAME_LENGTH}
          required
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'renameFolder'}
        title={copy.dataRoom.renameFolder}
        body={copy.dataRoom.renameFolderBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.renameFolderEffects}
        confirmLabel={copy.dataRoom.renameFolder}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={act}
      >
        <TextField
          label={copy.dataRoom.folderNameLabel}
          value={nameField}
          onChange={setNameField}
          maxLength={DATA_ROOM_MAX_FOLDER_NAME_LENGTH}
          required
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'moveFolder'}
        title={copy.dataRoom.moveFolder}
        body={copy.dataRoom.moveBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.moveEffects}
        confirmLabel={copy.dataRoom.moveFolder}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={act}
      >
        <ChoiceGroup
          legend={copy.dataRoom.destinationLabel}
          value={destination}
          onChange={setDestination}
          choices={destinationChoices({ movingFolder: targetFolder }).map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog === 'deleteFolder'}
        title={copy.dataRoom.deleteFolderTitle}
        body={copy.dataRoom.deleteFolderBody(targetFolder?.name ?? '')}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.deleteFolderEffects}
        confirmLabel={copy.dataRoom.deleteFolder}
        cancelLabel={copy.actions.cancel}
        danger
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={act}
      />

      <ConfirmDialog
        open={dialog === 'moveDocument'}
        title={copy.dataRoom.moveDocument}
        body={copy.dataRoom.moveBody}
        whatHappensLabel={copy.dialogs.whatHappens}
        effects={copy.dataRoom.moveEffects}
        confirmLabel={copy.dataRoom.moveDocument}
        cancelLabel={copy.actions.cancel}
        busy={busy}
        error={failureAlert}
        onCancel={closeDialog}
        onConfirm={act}
      >
        <ChoiceGroup
          legend={copy.dataRoom.destinationLabel}
          value={destination}
          onChange={setDestination}
          choices={destinationChoices({ movingFolder: null }).map((choice) => ({
            value: choice.value,
            label: choice.label,
          }))}
        />
      </ConfirmDialog>
    </>
  );
}
